import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import LayoutContainer from 'components/Common/LayoutContainer'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import moment from 'moment'
import { dateIsAfterOrSame } from 'lib/datetime/dateUtils'

const CruiseAlertBannerWrapper = styled.div`
  padding-top: ${rem(16)};
`

type AlertSchedule = {
  shipNames?: Array<string>
  rateCodes?: Array<string>
  departureDate?: Date
  startDateUTC: moment.Moment
  endDateUTC: moment.Moment
  cruiseLineCode: string
}

const ALERT_SCHEDULES: Array<AlertSchedule> = [
  {
    departureDate: new Date('2025-03-29'),
    startDateUTC: moment.utc('2024-11-20 00:00:00'), // 2024-11-20 11:00:00 Sydney time
    endDateUTC: moment.utc('2025-05-01 12:59:59'), // 2025-05-01 23:59:59 Sydney time
    cruiseLineCode: 'POA',
  },
  {
    startDateUTC: moment.utc('2025-01-30 04:00:00'), // 2025-01-29 23:00:00 ET
    endDateUTC: moment.utc('2025-01-31 04:00:00'), // 2025-01-30 23:00:00 ET
    cruiseLineCode: 'DCL',
  },
]

interface Props {
  offer: App.CruiseOffer
}

function CruiseAlertBanner({ offer }: Props) {
  const intl = useIntl()
  const offerRates = useMemo(
    () => Object.values(offer.lowestDeparturePricesByCategory)
      .flatMap((categories) => Object.values(categories).map(({ rateCode }) => rateCode)),
    [offer],
  )

  const {
    isAlertActive,
    hasShipInAlert,
    hasRateCodesInAlert,
    hasDepartureDateInAlert,
  } = useMemo(() => {
    const activeSchedules = ALERT_SCHEDULES.filter((rule) => (
      moment.utc().isBetween(rule.startDateUTC, rule.endDateUTC) &&
      rule.cruiseLineCode === offer.cruiseLine.code
    ))

    const hasShipInAlert = activeSchedules.some(({ shipNames }) => shipNames?.includes(offer.ship.name))
    const hasRateCodesInAlert = activeSchedules.some(({ rateCodes }) => rateCodes?.some((rateCode) => offerRates.includes(rateCode)))

    const hasDepartureDateInAlert = activeSchedules.some(({ departureDate }) => {
      if (!departureDate) {
        return false
      }

      return offer.departureDates?.some((date) => dateIsAfterOrSame(new Date(date), departureDate))
    })

    const isAlertActive = !!activeSchedules.length

    return {
      isAlertActive,
      hasShipInAlert,
      hasRateCodesInAlert,
      hasDepartureDateInAlert,
    }
  }, [offer.cruiseLine.code, offer.ship.name, offer.departureDates, offerRates])

  const description = useMemo(() => {
    if (hasShipInAlert) {
      return intl.formatMessage({
        id: 'h6UtbZy25rIg-v5tvztu',
        defaultMessage: '{shipName} sailings are currently undergoing scheduled maintenance and will resume soon. We appreciate your patience.',
        description: 'Alert message when specific ship sailings are under maintenance',
      }, { shipName: offer.ship.name })
    }

    if (hasRateCodesInAlert) {
      return intl.formatMessage({
        id: 'CaYjDtEUzu0ctBelwqup',
        defaultMessage: 'Some sailings are currently undergoing scheduled maintenance and will resume soon. We appreciate your patience.',
        description: 'Alert message when some sailings are under maintenance',
      })
    }

    if (hasDepartureDateInAlert) {
      return intl.formatMessage({
        id: '2OtysIDfIiSp_dYWwl_N',
        defaultMessage: 'From 29th of March, 2025, this itinerary and all other P&O itineraries will be operated by Carnival.',
        description: 'Alert message about itinerary operation change in the cruises section',
      })
    }

    return intl.formatMessage({
      id: 'ALB0xfbLmFFx_7plUQTR',
      defaultMessage: 'This cruise line is currently undergoing scheduled maintenance and will resume soon. We appreciate your patience.',
      description: 'Alert message when cruise line is under maintenance',
    })
  }, [hasShipInAlert, hasRateCodesInAlert, hasDepartureDateInAlert, offer.ship.name, intl])

  const heading = useMemo(() => {
    if (hasDepartureDateInAlert) {
      return intl.formatMessage({
        id: '1UU2jA2I0uhjOfe8PdoH',
        defaultMessage: '{cruiseLineName} - Additional Info',
        description: 'Alert banner heading for additional information in the cruises section',
      }, { cruiseLineName: offer.cruiseLine.name })
    }

    return intl.formatMessage({
      id: '-GR1okryE-gFLhdiXZvP',
      defaultMessage: '{cruiseLineName} - Scheduled Maintenance',
      description: 'Alert banner heading for scheduled maintenance in the cruises section',
    }, { cruiseLineName: offer.cruiseLine.name })
  }, [hasDepartureDateInAlert, offer.cruiseLine.name, intl])

  return <>
    {isAlertActive && <CruiseAlertBannerWrapper>
      <LayoutContainer>
        <MessageBanner kind="informational-primary" heading={heading}>{description}</MessageBanner>
      </LayoutContainer>
    </CruiseAlertBannerWrapper>}
  </>
}

export default CruiseAlertBanner
